import React, { useState, useCallback } from "react";
import { Column } from "@amzn/meridian";
import "src/components/pages/inspection/cameraFrame.scss";
import StreamingDevControls from "./streamingDevControls";
import DetectionOverlay from "./detectionOverlay";
import CaptureButton from "./components/CaptureButton";
import FlashlightButton from "./components/FlashlightButton";
import ZoomControl from "./components/ZoomControl";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { getConfigDrivenQuestionIds } from "./utils/questionIdExtractor";
import { analyzeEvidenceAsync } from "src/store/inspectionResultsSlice";
// Custom hooks
import { useDualStreamCapture } from "./hooks/useDualStreamCapture";
import { useStreamingService } from "./hooks/useStreamingService";

type CameraFrameProps = {
  onCapture: () => void;
  targetClasses: string[];
};

const CameraFrame = ({
  onCapture,
  targetClasses,
}: CameraFrameProps) => {
  // Redux state
  const dispatch = useAppDispatch();
  const {
    streamingDevMode,
    streamingConnected,
    detectionBoxes,
    streamingGuidanceMessage,
    imageNextStage,
    streamingConfig,
    scannedBarcode,
  } = useAppSelector((state) => state.inspectionCapture);
  
  // camera controls = capture button, zoom, flashlight
  const [ showCameraControls, setShowCameraControls ] = useState(true);
  const { inspectionId, currentStep, currentIterateStep } = useAppSelector((state) => state.workflow);
  
  // Dual camera system (with screen-based resolution)
  const {
    lowResVideoRef,
    highResVideoRef,
    captureHighResFrame,
    streamsReady,
  } = useDualStreamCapture(
    streamingConfig.resolutionScale,  // Low-res scale from config
    1.0  // High-res always 100% of screen
  );
  
  const { streamingService, pauseStreaming } = useStreamingService({
    lowResVideoRef,
    highResVideoRef,
    enabled: streamsReady,
    onReadyForAnalyzer: () => {
      setShowCameraControls(false);
      onCapture();
    },
    containerId: scannedBarcode || undefined,
    targetClasses: targetClasses,
  });
  
  // Video dimensions
  const videoWidth = lowResVideoRef.current?.videoWidth || 0;
  const videoHeight = lowResVideoRef.current?.videoHeight || 0;
  
  const captureCallback = useCallback(async () => {
    pauseStreaming();
    await handleCaptureInternal();
  }, [pauseStreaming]);

  const handleCaptureInternal = async () => {
    let capturedFile: File = await captureHighResFrame();;
    const questionIds = getConfigDrivenQuestionIds(currentStep, currentIterateStep);

    const analyzePayload = new FormData();
    analyzePayload.append("inspection_id", inspectionId);
    analyzePayload.append("file", capturedFile);
    questionIds?.forEach(qId => {
      analyzePayload.append("question_ids", qId);
    });
    if(scannedBarcode !== null && scannedBarcode !== undefined) {
      analyzePayload.append("object_id", scannedBarcode);
    }
    await dispatch(analyzeEvidenceAsync(analyzePayload)).unwrap();
    setShowCameraControls(false);
    onCapture();
  };

  return (
    <Column className="camera-frame-viewport">
      {/* Display low-res video directly (high-res hidden) */}
      {(
        <>
          <video
            ref={lowResVideoRef}
            className="image-source-video"
            autoPlay
            muted
            playsInline
          />
          <video
            ref={highResVideoRef}
            autoPlay
            muted
            playsInline
            style={{ display: 'none' }}
          />
        </>
      )}
      
      <div className="camera-frame-wrapper">
        {(
          <Column className="camera-frame">
            <Column className="top-line-camera-frame" />
            <Column className="left-line-camera-frame" />
            <Column className="right-line-camera-frame" />
            <Column className="bottom-line-camera-frame" />
          </Column>
        )}
        
      {/* Detection Overlay */}
      {lowResVideoRef.current && (
        <DetectionOverlay 
          detectionBoxes={detectionBoxes}
          videoWidth={videoWidth}
          videoHeight={videoHeight}
          imageNextStage={imageNextStage}
          guidanceMessage={streamingGuidanceMessage}
        />
      )}
      </div>
      
      {/* Dev Mode Controls */}
      {streamingDevMode && streamingService && (
        <StreamingDevControls streamingService={streamingService} />
      )}
      
      {/* Connection Status */}
      {streamingDevMode && (
        <div className={`connection-status ${streamingConnected ? 'connected' : 'disconnected'}`}>
          {streamingConnected ? 
            (streamingService?.isStreamingPaused() ? 'Connected (Paused)' : 'Connected') : 
            'Connecting...'}
        </div>
      )}
      
      {/* Camera controls */}
      {showCameraControls &&  (
        <>
          <CaptureButton
            onClick={captureCallback}
          />
          <FlashlightButton 
            videoRef={lowResVideoRef}
            disabled={false}
          />
          <ZoomControl 
            videoRef={lowResVideoRef}
            disabled={false}
          />
        </>
      )}
    </Column>
  );
};

export default CameraFrame;
